import { apiClient } from './client'
import type { TryoutHomeEvent } from './tryoutHome'

/** Required V2 contract: docs/athlete-feedback-v2.md. No legacy fallback. */
export interface AthleteFeedbackRecord {
  participant: { id: string; name: string; number?: number | null }
  version: string
  assignment?: {
    assigneeId: string
    assigneeName: string
    assignedByName?: string
    note?: string
    assignedAt: string
    completedAt: string | null
  } | null
  assignedToMe?: boolean
  request: {
    requestedAt: string
    information: string
    overdue: boolean
    reminderSentAt: string | null
  } | null
  feedback: {
    html: string
    published: boolean
    publishedAt: string | null
    authors: string[]
  } | null
  permissions: {
    assign?: boolean
    request: boolean
    edit: boolean
    publish: boolean
    withdraw: boolean
  }
}
export interface AthleteFeedbackWorkspace {
  assistants?: Array<{ id: string; name: string }>
  outstandingCount?: number
  view: 'athlete' | 'staff'
  event: TryoutHomeEvent
  requestPolicy: {
    minimumCharacters: number
    sessionsConcluded: boolean
    allowEarlyFeedback: boolean
    staffNoun: string
  }
  records: AthleteFeedbackRecord[]
  requestCandidates: Array<{ id: string; name: string; number?: number | null }>
  permissions: { addRequest: boolean }
}
export interface FeedbackScope {
  clubId: string
  seasonId: string
  tryoutId: string
}
export type FeedbackAiTone =
  | 'encouraging'
  | 'direct'
  | 'supportive'
  | 'professional'
export type FeedbackAiPurpose =
  | 'development'
  | 'trial-outcome'
  | 'role-development'
  | 'next-steps'
export interface FeedbackAiFunding {
  id: string
  source: 'club' | 'personal'
  cost: number
  balance: number
}
export interface FeedbackAiOptions {
  funding: FeedbackAiFunding[]
  tones: FeedbackAiTone[]
  purposes: FeedbackAiPurpose[]
}
export interface FeedbackAiCommand {
  requestId: string
  version: string
  fundingId: string
  expectedCost: number
  tone: FeedbackAiTone
  purpose: FeedbackAiPurpose
  locale: string
  draft: string
}
export interface FeedbackAiSuggestion {
  id: string
  state: 'pending' | 'completed' | 'failed'
  feedbackVersion: string
  funding: Pick<FeedbackAiFunding, 'id' | 'source' | 'cost'>
  result: { text: string; evidenceNotes: string[] } | null
  errorCode: string | null
}
function validateAiOptions(data: FeedbackAiOptions) {
  if (
    !Array.isArray(data?.funding) ||
    !Array.isArray(data.tones) ||
    !Array.isArray(data.purposes) ||
    data.funding.some(
      (f) =>
        !f.id ||
        !['club', 'personal'].includes(f.source) ||
        !Number.isInteger(f.cost) ||
        f.cost < 0 ||
        !Number.isInteger(f.balance) ||
        f.balance < 0
    )
  )
    throw new Error('Invalid feedback AI options')
  return data
}
function validateAiSuggestion(data: FeedbackAiSuggestion) {
  if (
    !data?.id ||
    !['pending', 'completed', 'failed'].includes(data.state) ||
    typeof data.feedbackVersion !== 'string' ||
    !data.funding ||
    (data.state === 'completed' &&
      (typeof data.result?.text !== 'string' ||
        !Array.isArray(data.result.evidenceNotes) ||
        data.result.evidenceNotes.some((n) => typeof n !== 'string')))
  )
    throw new Error('Invalid feedback AI suggestion')
  return data
}
function validateRecord(data: AthleteFeedbackRecord): AthleteFeedbackRecord {
  if (
    !data?.participant?.id ||
    typeof data.participant.name !== 'string' ||
    typeof data.version !== 'string' ||
    !data.permissions ||
    !['request', 'edit', 'publish', 'withdraw'].every(
      (key) =>
        typeof data.permissions[key as keyof typeof data.permissions] ===
        'boolean'
    ) ||
    (data.feedback &&
      (typeof data.feedback.html !== 'string' ||
        typeof data.feedback.published !== 'boolean' ||
        !Array.isArray(data.feedback.authors)))
  )
    throw new Error('Invalid athlete feedback record')
  return data
}
export function athleteFeedbackAPI(scope: FeedbackScope) {
  const base = `/v2/season/${encodeURIComponent(scope.seasonId)}/tryouts/${encodeURIComponent(scope.tryoutId)}/athlete-feedback`
  const query = { clubId: scope.clubId }
  const participantPath = (id: string) =>
    `${base}/participants/${encodeURIComponent(id)}`
  return {
    aiOptions: (id: string, signal?: AbortSignal) =>
      apiClient
        .get<FeedbackAiOptions>(
          `${participantPath(id)}/ai-suggestions/loadFeedbackAiOptions`,
          query,
          signal
        )
        .then(validateAiOptions),
    aiGenerate: (id: string, command: FeedbackAiCommand) =>
      apiClient
        .post<FeedbackAiSuggestion>(
          `${participantPath(id)}/ai-suggestions`,
          command,
          query
        )
        .then(validateAiSuggestion),
    aiLoad: (id: string, requestId: string) =>
      apiClient
        .get<FeedbackAiSuggestion>(
          `${participantPath(id)}/ai-suggestions/${encodeURIComponent(requestId)}/loadFeedbackAiSuggestion`,
          query
        )
        .then(validateAiSuggestion),
    async load(view: AthleteFeedbackWorkspace['view'], signal?: AbortSignal) {
      const data = await apiClient.get<AthleteFeedbackWorkspace>(
        `${base}/loadAthleteFeedback`,
        { ...query, view },
        signal
      )
      // Never adapt the old event-wide feedback response into an athlete response.
      if (
        data?.view !== view ||
        !data.event ||
        !data.requestPolicy ||
        !Number.isInteger(data.requestPolicy.minimumCharacters) ||
        data.requestPolicy.minimumCharacters < 20 ||
        !Array.isArray(data.records) ||
        !Array.isArray(data.requestCandidates) ||
        !data.permissions ||
        (view === 'athlete' &&
          (data.records.length !== 1 ||
            data.requestCandidates.length > 0 ||
            data.records.some((r) => r.feedback && !r.feedback.published)))
      ) {
        throw new Error('Invalid athlete feedback workspace')
      }
      data.records.forEach(validateRecord)
      return data
    },
    request: (
      participantId: string,
      information: string,
      view: AthleteFeedbackWorkspace['view'] = 'athlete'
    ) =>
      apiClient
        .post<AthleteFeedbackRecord>(
          `${base}/requests`,
          { participantId, information },
          { ...query, view }
        )
        .then(validateRecord),
    assign: (id: string, assigneeId: string, version: string, note = '') =>
      apiClient
        .put<AthleteFeedbackRecord>(
          `${participantPath(id)}/assignment`,
          { assigneeId, version, note },
          query
        )
        .then(validateRecord),
    save: (id: string, html: string, version: string) =>
      apiClient
        .put<AthleteFeedbackRecord>(
          `${participantPath(id)}/draft`,
          { html, version },
          query
        )
        .then(validateRecord),
    publish: (id: string, version: string) =>
      apiClient
        .post<AthleteFeedbackRecord>(
          `${participantPath(id)}/publication`,
          { version },
          query
        )
        .then(validateRecord),
    withdraw: (id: string, version: string) =>
      apiClient
        .delete<AthleteFeedbackRecord>(`${participantPath(id)}/publication`, {
          ...query,
          version,
        })
        .then(validateRecord),
  }
}
